import { useCallback, useState } from 'react';

import { useToast } from '@contexts/Toast.context';
import { fetchUrl } from '@utils/api';
import { downloadFile } from '@utils/files';

/**
 * Runs one server-side collection export and saves the resulting file.
 *
 * Both collection exports — detections (GeoJSON) and sampling plots (zip) —
 * answer with a storage key, never with the file body. So every download is the
 * same three steps, and this hook owns them once for both call sites:
 *
 *   request() → key → `filelink?key=…` → signed link → downloadFile()
 *
 * That is the key-to-file path the app already takes elsewhere (the model
 * path's `getDetections`, the photo layer's tiles), rather than a second one.
 *
 * Empty and failed exports are toasts, not thrown errors: a download is a leaf
 * action, and there is nothing above it to recover. A `null` key means the
 * export endpoint answered 204 — a detected area exists, but no detection
 * passes the filter, and the server wrote no file. The user is told that
 * instead of being handed an empty file.
 *
 * The caller keeps the filter out of here: it passes `request` already bound to
 * the filter body it built, so this hook never reasons about filters.
 *
 * @returns {{ isDownloading: boolean, download: (params: {
 *   request: () => Promise<string|null>,
 *   title: string,
 *   fileExtension: string,
 *   emptyMessage: string,
 *   errorMessage: string,
 * }) => Promise<void> }}
 */
const useCollectionExportDownload = () => {
	const { addToast } = useToast();
	const [isDownloading, setIsDownloading] = useState(false);

	const download = useCallback(
		async ({ request, title, fileExtension, errorMessage, emptyMessage }) => {
			setIsDownloading(true);
			try {
				const key = await request();
				// 204 — nothing passed the filter, and the server wrote no file.
				if (!key) {
					addToast({ title: emptyMessage, bg: 'warning' });
					return;
				}

				const { url } = (await fetchUrl(`filelink?key=${key}`)) ?? {};
				if (!url) throw new Error('No signed link returned for the export key');

				// The signed link points at S3, another origin, and a browser ignores
				// an anchor's `download` on a cross-origin href: it navigates instead,
				// and the user reads the GeoJSON in a tab rather than saving it. The
				// link cannot carry a content-disposition either — SigV4 signs the
				// query string, so a parameter added here breaks the signature. So
				// the body is fetched and saved from a same-origin blob URL, the same
				// route useHexagonExport takes for its KML/GeoJSON.
				const response = await fetch(url);
				if (!response.ok) throw new Error(`${response.status}: could not fetch the export`);
				const objectUrl = URL.createObjectURL(await response.blob());

				downloadFile({ url: objectUrl, title, fileExtension });
				addToast({ title: 'Download started', bg: 'success' });
				// Revoked on a delay rather than at once: some browsers process the
				// anchor's click() asynchronously, and an immediate revoke cancels the
				// download in flight.
				setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
			} catch (err) {
				console.error('Collection export error:', err);
				addToast({ title: errorMessage, bg: 'danger' });
			} finally {
				setIsDownloading(false);
			}
		},
		[addToast]
	);

	return { isDownloading, download };
};

export default useCollectionExportDownload;
